import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { TrackingSettings } from '../types'
import { apiService } from '../services/api'

declare global {
  interface Window {
    dataLayer?: any[]
    gtag?: (...args: any[]) => void
    fbq?: any
    _fbq?: any
    ttq?: any
    TiktokAnalyticsObject?: string
    _linkedin_partner_id?: string
    _linkedin_data_partner_ids?: string[]
    lintrk?: any
    pintrk?: any
  }
}

/** Append a vendor script once, keyed so a re-render never loads it twice. */
function loadScript(key: string, src: string) {
  if (document.querySelector(`script[data-tracking="${key}"]`)) return
  const script = document.createElement('script')
  script.async = true
  script.src = src
  script.dataset.tracking = key
  document.head.appendChild(script)
}

/** gtag.js backs both Google Analytics and Google Ads, so it loads at most once. */
function ensureGtag(primaryId: string) {
  window.dataLayer = window.dataLayer || []
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments)
    }
    window.gtag('js', new Date())
  }
  loadScript('gtag', `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`)
}

function initGoogleTagManager(containerId: string) {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })
  loadScript('gtm', `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`)

  if (!document.querySelector('noscript[data-tracking="gtm"]')) {
    const noscript = document.createElement('noscript')
    noscript.dataset.tracking = 'gtm'
    const iframe = document.createElement('iframe')
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(containerId)}`
    iframe.height = '0'
    iframe.width = '0'
    iframe.style.display = 'none'
    iframe.style.visibility = 'hidden'
    noscript.appendChild(iframe)
    document.body.appendChild(noscript)
  }
}

function initMetaPixel(pixelId: string) {
  if (!window.fbq) {
    const fbq: any = function (...args: any[]) {
      fbq.callMethod ? fbq.callMethod.apply(fbq, args) : fbq.queue.push(args)
    }
    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.queue = []
    window.fbq = fbq
    window._fbq = window._fbq || fbq
    loadScript('meta-pixel', 'https://connect.facebook.net/en_US/fbevents.js')
  }
  window.fbq('init', pixelId)
  window.fbq('track', 'PageView')
}

function initTikTokPixel(pixelId: string) {
  const ttq: any = window.ttq || []
  if (!window.ttq) {
    window.TiktokAnalyticsObject = 'ttq'
    ttq.methods = [
      'page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once',
      'ready', 'alias', 'group', 'enableCookie', 'disableCookie',
    ]
    ttq.setAndDefer = (target: any, method: string) => {
      target[method] = (...args: any[]) => target.push([method].concat(args))
    }
    ttq.methods.forEach((method: string) => ttq.setAndDefer(ttq, method))
    ttq.load = (id: string) => {
      ttq._i = ttq._i || {}
      ttq._i[id] = []
      ttq._t = ttq._t || {}
      ttq._t[id] = Date.now()
      ttq._o = ttq._o || {}
      loadScript('tiktok-pixel', `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${encodeURIComponent(id)}&lib=ttq`)
    }
    window.ttq = ttq
  }
  ttq.load(pixelId)
  ttq.page()
}

function initLinkedInInsight(partnerId: string) {
  window._linkedin_partner_id = partnerId
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || []
  if (!window._linkedin_data_partner_ids.includes(partnerId)) {
    window._linkedin_data_partner_ids.push(partnerId)
  }
  if (!window.lintrk) {
    const queue: any[] = []
    const lintrk: any = (action: string, payload: any) => queue.push([action, payload])
    lintrk.q = queue
    window.lintrk = lintrk
    loadScript('linkedin-insight', 'https://snap.licdn.com/li.lms-analytics/insight.min.js')
  }
}

function initPinterestTag(tagId: string) {
  if (!window.pintrk) {
    const pintrk: any = (...args: any[]) => pintrk.queue.push(args)
    pintrk.queue = []
    pintrk.version = '3.0'
    window.pintrk = pintrk
    loadScript('pinterest-tag', 'https://s.pinimg.com/ct/core.js')
  }
  window.pintrk('load', tagId)
  window.pintrk('page')
}

/** True when the account has at least one pixel configured. */
export function hasAnyTracking(tracking: TrackingSettings | null | undefined): boolean {
  return !!tracking && Object.values(tracking).some((value) => !!value)
}

/**
 * Loads the auctioneer's own analytics and advertising pixels on their public
 * pages, and reports a page view on every client-side navigation - without it
 * a single-page app only ever reports the first URL a visitor lands on.
 *
 * The account is resolved from the custom domain, falling back to the account
 * that owns the auction being viewed on the shared domain.
 */
export function useTrackingScripts(accountId?: string) {
  const [tracking, setTracking] = useState<TrackingSettings | null>(null)
  const location = useLocation()
  const initialisedRef = useRef(false)
  const lastPathRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const response = await apiService.getPublicTracking(accountId)
        if (!cancelled && response.success) {
          setTracking(response.data?.tracking || null)
        }
      } catch {
        // Tracking is never worth breaking a page over
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [accountId])

  // Install the vendor loaders once per page load
  useEffect(() => {
    if (!hasAnyTracking(tracking) || initialisedRef.current) return
    initialisedRef.current = true

    const googleIds = [tracking!.googleAnalyticsId, tracking!.googleAdsConversionId].filter(Boolean) as string[]
    if (googleIds.length > 0) {
      ensureGtag(googleIds[0])
      googleIds.forEach((id) => window.gtag!('config', id))
    }
    if (tracking!.googleTagManagerId) initGoogleTagManager(tracking!.googleTagManagerId)
    if (tracking!.metaPixelId) initMetaPixel(tracking!.metaPixelId)
    if (tracking!.tiktokPixelId) initTikTokPixel(tracking!.tiktokPixelId)
    if (tracking!.linkedInPartnerId) initLinkedInInsight(tracking!.linkedInPartnerId)
    if (tracking!.pinterestTagId) initPinterestTag(tracking!.pinterestTagId)
  }, [tracking])

  // Report subsequent route changes; each vendor's loader already counts the first view
  useEffect(() => {
    if (!hasAnyTracking(tracking)) return

    const path = `${location.pathname}${location.search}`
    if (lastPathRef.current === null) {
      lastPathRef.current = path
      return
    }
    if (lastPathRef.current === path) return
    lastPathRef.current = path

    const googleIds = [tracking!.googleAnalyticsId, tracking!.googleAdsConversionId].filter(Boolean) as string[]
    googleIds.forEach((id) =>
      window.gtag?.('config', id, { page_path: path, page_location: window.location.href })
    )
    window.fbq?.('track', 'PageView')
    window.ttq?.page?.()
    window.pintrk?.('page')
  }, [location.pathname, location.search, tracking])

  return tracking
}
